import React from "react";
import { useTranslation } from "react-i18next";

function SponsoringComparison() {
  const { t } = useTranslation();

  const packages = [
    { key: "gold", label: t("sponsoringPackagesComparison.packages.gold") },
    { key: "silver", label: t("sponsoringPackagesComparison.packages.silver") },
    { key: "bronze", label: t("sponsoringPackagesComparison.packages.bronze") }
  ];

  const packageIconByKey = {
    bronze: "fa-award",
    silver: "fa-medal",
    gold: "fa-trophy",
  };

  const aspectIconByKey = {
    investment: "fa-coins",
    logoWebsite: "fa-globe",
    eventMaterials: "fa-gift",
    printMedia: "fa-newspaper",
    socialMedia: "fa-hashtag",
    commercials: "fa-bullhorn",
    booth: "fa-store",
    vipArea: "fa-crown",
  };

  const aspects = [
    { key: "investment", label: t("sponsoringPackagesComparison.aspects.investment") },
    { key: "logoWebsite", label: t("sponsoringPackagesComparison.aspects.logoWebsite") },
    { key: "eventMaterials", label: t("sponsoringPackagesComparison.aspects.eventMaterials") },
    { key: "printMedia", label: t("sponsoringPackagesComparison.aspects.printMedia") },
    { key: "socialMedia", label: t("sponsoringPackagesComparison.aspects.socialMedia") },
    { key: "commercials", label: t("sponsoringPackagesComparison.aspects.commercials") },
    { key: "booth", label: t("sponsoringPackagesComparison.aspects.booth") },
    { key: "vipArea", label: t("sponsoringPackagesComparison.aspects.vipArea") }
  ];
  

  const getCellText = (pkgKey, aspectKey) => {
    const key = `sponsoringPackagesComparison.cells.${pkgKey}.${aspectKey}`;
    const val = t(key);
    if (!val || val === key) return "";
    return val;
  };

  return (
    <section className="pt-12 pb-12 bg-slate-50/50 dark:bg-slate-900 transition-colors relative z-10" id="sponsoring-packages">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-8 text-center">
          <h2 className="text-4xl md:text-5xl mb-5 text-primary dark:text-primary-light relative inline-block">
            {t("sponsoringPackagesComparison.title")}
            {/* Underline decoration */}
            <div className="absolute -bottom-2.5 left-1/2 transform -translate-x-1/2 w-15 h-0.5 bg-primary dark:bg-primary-light rounded-sm" />
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mt-4 text-lg max-w-3xl mx-auto">
            {t("sponsoringPackagesComparison.description")}
          </p>
        </div>

        {/* Desktop Table */}
        <div className="hidden lg:block mb-6">
          {/* Separate Header Row - komplett außerhalb der Tabelle, KEINE Zelle über erster Spalte */}
          <div className="flex mb-0">
            {/* Leerer Spacer - KEINE Zelle, nur Platzhalter für erste Spalte */}
            <div className="flex-shrink-0" style={{ width: '256px', minWidth: '256px' }}></div>
            {/* Package Headers mit abgerundeten oberen Ecken */}
            {packages.map((p, index) => (
              <div
                key={p.key}
                className={`px-6 py-4 text-center border-t border-slate-200 dark:border-slate-700 ${
                  index === 0 ? 'border-l border-slate-200 dark:border-slate-700' : 'border-l border-slate-200 dark:border-slate-700'
                } ${
                  index === packages.length - 1 ? 'border-r border-slate-200 dark:border-slate-700' : ''
                } ${
                  p.key === 'gold' ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-900 dark:text-yellow-300' :
                  p.key === 'silver' ? 'bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-300' :
                  'bg-orange-50 dark:bg-orange-900/20 text-orange-900 dark:text-orange-300'
                }`}
                style={{
                  width: p.key === 'gold' ? 'calc((100% - 256px) / 3)' : p.key === 'silver' ? 'calc((100% - 256px) / 3)' : 'calc((100% - 256px) / 3 + 2px)',
                  marginLeft: p.key === 'gold' ? '1px' : '0',
                  borderTopLeftRadius: (p.key === 'gold' || p.key === 'silver' || p.key === 'bronze') ? '0.5rem' : '0',
                  borderTopRightRadius: (p.key === 'gold' || p.key === 'silver' || p.key === 'bronze') ? '0.5rem' : '0'
                }}
                role="columnheader"
                aria-label={p.label}
              >
                <div className="flex flex-col items-center gap-2">
                  <i className={`fas ${packageIconByKey[p.key]} text-2xl`} aria-hidden="true"></i>
                  <span className="text-sm font-semibold">{p.label}</span>
                  {t(`sponsoringPackagesComparison.packages.${p.key}Subtitle`, '') && (
                    <span className={`text-xs ${
                      p.key === 'gold' ? 'text-yellow-700 dark:text-yellow-400' :
                      p.key === 'silver' ? 'text-gray-700 dark:text-gray-400' :
                      'text-orange-700 dark:text-orange-400'
                    }`}>
                      {t(`sponsoringPackagesComparison.packages.${p.key}Subtitle`, '')}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Data Table - komplett separate Box */}
          <div className="bg-white dark:bg-slate-800 rounded-tl-lg rounded-bl-lg rounded-br-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse table-fixed" role="table" aria-label={t("sponsoringPackagesComparison.title")}>
                <colgroup>
                  <col style={{ width: '256px' }} />
                  <col style={{ width: 'calc((100% - 256px) / 3)' }} />
                  <col style={{ width: 'calc((100% - 256px) / 3)' }} />
                  <col style={{ width: 'calc((100% - 256px) / 3)' }} />
                </colgroup>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {aspects.map((a) => (
                    <tr key={a.key} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/30 transition-colors" role="row">
                      <td className="px-6 py-4 text-sm font-medium text-slate-900 dark:text-slate-100 sticky left-0 bg-white dark:bg-slate-800 z-10 border-r border-slate-200 dark:border-slate-700" style={{ width: '256px', minWidth: '256px' }} role="rowheader">
                        <div className="flex items-center gap-3">
                          <i className={`fas ${aspectIconByKey[a.key] || "fa-circle"} text-primary dark:text-primary-light`} aria-hidden="true"></i>
                          <span>{a.label}</span>
                        </div>
                      </td>
                      {packages.map((p, index) => {
                        const text = getCellText(p.key, a.key);
                        return (
                          <td 
                            key={`${p.key}-${a.key}`} 
                            className={`px-6 py-4 text-center text-sm text-slate-700 dark:text-slate-300 border-r border-slate-200 dark:border-slate-700 ${
                              index === packages.length - 1 ? 'border-r-0' : ''
                            }`} 
                            role="cell"
                          >
                            {text && text.trim() !== "" ? text : "—"}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {packages.map((pkg) => (
            <div 
              key={pkg.key} 
              className={`bg-white dark:bg-slate-800 rounded-lg border shadow-sm overflow-hidden ${
                pkg.key === 'gold' ? 'border-yellow-200 dark:border-yellow-800' :
                pkg.key === 'silver' ? 'border-gray-200 dark:border-gray-700' :
                'border-orange-200 dark:border-orange-800'
              }`}
            >
              <div className={`p-6 ${
                pkg.key === 'gold' ? 'bg-yellow-50 dark:bg-yellow-900/20' :
                pkg.key === 'silver' ? 'bg-gray-50 dark:bg-gray-700' :
                'bg-orange-50 dark:bg-orange-900/20'
              }`}>
                <div className="flex items-center gap-3 mb-2">
                  <i className={`fas ${packageIconByKey[pkg.key]} text-2xl ${
                    pkg.key === 'gold' ? 'text-yellow-600 dark:text-yellow-400' :
                    pkg.key === 'silver' ? 'text-gray-600 dark:text-gray-400' :
                    'text-orange-600 dark:text-orange-400'
                  }`} aria-hidden="true"></i>
                  <h3 className={`text-lg font-semibold ${
                    pkg.key === 'gold' ? 'text-yellow-900 dark:text-yellow-300' :
                    pkg.key === 'silver' ? 'text-gray-900 dark:text-gray-300' :
                    'text-orange-900 dark:text-orange-300'
                  }`}>
                    {pkg.label}
                  </h3>
                </div>
                {t(`sponsoringPackagesComparison.packages.${pkg.key}Subtitle`, '') && (
                  <p className={`text-sm ${
                    pkg.key === 'gold' ? 'text-yellow-700 dark:text-yellow-400' :
                    pkg.key === 'silver' ? 'text-gray-700 dark:text-gray-400' :
                    'text-orange-700 dark:text-orange-400'
                  }`}>
                    {t(`sponsoringPackagesComparison.packages.${pkg.key}Subtitle`, '')}
                  </p>
                )}
              </div>
              
              <div className="p-6 space-y-4">
                {aspects.map((aspect) => {
                  const text = getCellText(pkg.key, aspect.key);
                  
                  return (
                    <div key={aspect.key} className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 dark:bg-primary-light/20 flex items-center justify-center">
                        <i className={`fas ${aspectIconByKey[aspect.key] || "fa-circle"} text-primary dark:text-primary-light text-sm`} aria-hidden="true"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-1">{aspect.label}</div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">
                          {text && text.trim() !== "" ? text : "—"}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Footnote */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <i className="fas fa-info-circle text-blue-600 dark:text-blue-400 mt-0.5" aria-hidden="true"></i>
            <p className="text-sm text-blue-800 dark:text-blue-300">
              {t("sponsoringPackagesComparison.footnote")}
            </p>
          </div>
        </div>

        {/* CTA Button */}
        <div className="flex justify-center">
          <a
            href="/documents/Event-Mappe.pdf"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-md hover:bg-primary-dark dark:hover:bg-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 shadow-sm hover:shadow-md"
            download="GCWAA-Event-Mappe.pdf"
            rel="noopener noreferrer"
          >
            <i className="fas fa-download"></i>
            {t("sponsoringBenefits.downloadEventmappe")}
          </a>
        </div>
      </div>
    </section>
  );
}

export default SponsoringComparison;

